/**
 * Пустое состояние: один мотив на все пустые места приложения (ТЗ UI-68).
 *
 * Пустой раздел говорил о себе строкой приглушённого текста — и выглядел так
 * же, как ошибка, которую забыли оформить. Здесь у пустоты своя форма: три
 * пустые клавиши, короткий заголовок и одна поясняющая строка.
 *
 * Путь дальше — только если он и правда есть. Кнопка, ведущая в никуда, хуже
 * молчания: она обещает выход, которого нет (ТЗ FR-GM-08).
 */
import type { ReactNode } from 'react';

export interface EmptyStateProps {
  /** Короткий заголовок: что именно пусто. */
  readonly title: string;
  /** Одна строка о том, почему пусто и что это меняет. */
  readonly body?: string | undefined;
  /** Действие, которое здесь и вправду существует; иначе не передаётся. */
  readonly action?: ReactNode;
}

/**
 * Три пустые клавиши.
 *
 * Рисунок декоративен и скрыт от чтения с экрана: рядом стоит тот же смысл
 * словами. Контур берёт `currentColor`, поэтому тема перекрашивает его сама.
 */
function EmptyArt(): React.JSX.Element {
  const KEY = 26;
  const GAP = 6;

  return (
    <svg
      className="t2x-empty__art"
      viewBox={`0 0 ${3 * KEY + 2 * GAP} ${KEY}`}
      width={3 * KEY + 2 * GAP}
      height={KEY}
      aria-hidden="true"
      focusable="false"
    >
      {[0, 1, 2].map((index) => (
        <rect
          key={index}
          x={index * (KEY + GAP) + 0.5}
          y={0.5}
          width={KEY - 1}
          height={KEY - 1}
          rx={5}
          fill="none"
          stroke="currentColor"
        />
      ))}
    </svg>
  );
}

export function EmptyState({ title, body, action }: EmptyStateProps): React.JSX.Element {
  return (
    <div className="t2x-empty">
      <EmptyArt />
      <p className="t2x-empty__title">{title}</p>
      {body !== undefined && <p className="t2x-empty__body">{body}</p>}
      {action}
    </div>
  );
}
